import Big from 'big.js';
import { useCallback, useMemo, useState } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';

import { TradingPairContractType } from 'common/api/autogenerated/trading-gateway';

import Logger from 'common/services/Logger/Logger';
import Storage from 'common/services/Storage/Storage';

import {
  INVERSED_FEE_DIGITS,
  INVERSED_PNL_DIGITS,
  LINEAR_FEE_DIGITS,
  LINEAR_PNL_DIGITS,
} from 'views/derivatives/constants';

import { detectPrecision } from 'utils/detectPrecision';
import { truncateNumber } from 'utils/truncateNumber';

import { actionInvokeSignalR } from 'blocks/SocketProvider/redux/socket.actions';

import config from './config';

const storage = new Storage('FUTURES');
const log = new Logger('FUTURES');

export const useDerivatives = (code: string | undefined | null = '') => {
  const dispatch = useDispatch();

  const { derivatives, groups, favorites, loading } = useSelector(
    (state: DefaultRootState) => state.derivativesReducer,
  );

  const unsubscribe = useCallback(
    (symbol: string, id: string) => {
      if (storage.isLast(symbol)) {
        log.warn('[unsubscribe] =>', symbol);
        dispatch(actionInvokeSignalR({ ...config.unsubscribeConfig(symbol), ref: id }));
      }

      storage.remove(symbol);
    },
    [dispatch],
  );

  const subscribe = useCallback(
    (symbol: string) => {
      const id = v4();

      if (!storage.isExist(symbol)) {
        log.success('[subscribe] =>', symbol);
        dispatch(actionInvokeSignalR({ ...config.subscribeConfig(symbol), id }));
      }

      storage.add(symbol);

      return {
        unsubscribe: () => unsubscribe(symbol, id),
      };
    },
    [dispatch, unsubscribe],
  );

  const stream = useMemo(() => derivatives[code || ''] || {}, [code, derivatives]);

  const digitsForBase = useMemo(
    () =>
      stream?.contractType === TradingPairContractType.LINEAR
        ? detectPrecision(new Big(stream?.contractValue ?? 0).mul(stream?.minOrderSize ?? 0).toNumber())
        : 4,
    [stream?.contractType, stream?.contractValue, stream?.minOrderSize],
  );

  const digitsForQuoted = useMemo(
    () => (stream?.baseCurrency === stream?.settlementCurrency ? digitsForBase : stream?.digits) || 0,
    [digitsForBase, stream?.baseCurrency, stream?.digits, stream?.settlementCurrency],
  );

  const digitsForPandL = useMemo(
    () => (stream?.contractType === TradingPairContractType.LINEAR ? LINEAR_PNL_DIGITS : INVERSED_PNL_DIGITS),
    [stream?.contractType],
  );

  const digitsForFees = useMemo(
    () => (stream?.contractType === TradingPairContractType.LINEAR ? LINEAR_FEE_DIGITS : INVERSED_FEE_DIGITS),
    [stream?.contractType],
  );

  const digitsForPrices = useMemo(() => stream?.digits, [stream?.digits]);

  const [targetPrice] = useState(stream?.price);

  const minQuantity = useMemo(() => {
    const _minQuantity = new Big(stream?.contractValue ?? 0).mul(stream?.minOrderSize ?? 0);

    const calculation = _minQuantity
      .div(stream?.contractType === TradingPairContractType.LINEAR ? 1 : targetPrice || 1)
      .toNumber();

    return truncateNumber(calculation, digitsForBase);
  }, [digitsForBase, stream?.contractType, stream?.contractValue, stream?.minOrderSize, targetPrice]);

  const maxMarketQuantity = useMemo(() => {
    const _maxMarketQuantity = new Big(stream?.contractValue ?? 0).mul(stream?.maxMarketOrderSize ?? 0);

    const calculation = _maxMarketQuantity
      .div(stream?.contractType === TradingPairContractType.LINEAR ? 1 : targetPrice || 1)
      .toNumber();

    return truncateNumber(calculation, digitsForBase);
    // minQuantity * (stream?.maxMarketOrderSize || 0)
  }, [digitsForBase, stream?.contractType, stream?.contractValue, stream?.maxMarketOrderSize, targetPrice]);
  const maxLimitQuantity = useMemo(() => {
    const _maxLimitQuantity = new Big(stream?.contractValue ?? 0).mul(stream?.maxLimitOrderSize ?? 0);

    const calculation = _maxLimitQuantity
      .div(stream?.contractType === TradingPairContractType.LINEAR ? 1 : targetPrice || 1)
      .toNumber();

    return truncateNumber(calculation, digitsForBase);
  }, [digitsForBase, stream?.contractType, stream?.contractValue, stream?.maxLimitOrderSize, targetPrice]);
  const maxStopMarketQuantity = useMemo(() => {
    const _maxStopMarketQuantity = new Big(stream?.contractValue ?? 0).mul(stream?.maxStopOrderSize ?? 0);

    const calculation = _maxStopMarketQuantity
      .div(stream?.contractType === TradingPairContractType.LINEAR ? 1 : targetPrice || 1)
      .toNumber();

    return truncateNumber(calculation, digitsForBase);
  }, [digitsForBase, stream?.contractType, stream?.contractValue, stream?.maxStopOrderSize, targetPrice]);
  const maxStopLimitQuantity = useMemo(() => {
    const _maxStopLimitQuantity = new Big(stream?.contractValue ?? 0).mul(stream?.maxTriggerOrderSize ?? 0);

    const calculation = _maxStopLimitQuantity
      .div(stream?.contractType === TradingPairContractType.LINEAR ? 1 : targetPrice || 1)
      .toNumber();

    return truncateNumber(calculation, digitsForBase);
  }, [digitsForBase, stream?.contractType, stream?.contractValue, stream?.maxTriggerOrderSize, targetPrice]);

  const info = useMemo(
    () => ({
      minQuantity,
      maxMarketQuantity,
      maxLimitQuantity,
      maxStopMarketQuantity,
      maxStopLimitQuantity,
      digitsForBase,
      digitsForQuoted,
      digitsForPrices,
      digitsForPandL,
      digitsForFees,
    }),
    [
      digitsForBase,
      digitsForQuoted,
      maxLimitQuantity,
      maxMarketQuantity,
      maxStopLimitQuantity,
      maxStopMarketQuantity,
      minQuantity,
      digitsForPrices,
      digitsForPandL,
      digitsForFees,
    ],
  );

  const result = useMemo(
    () => ({
      derivatives,
      groups,
      favorites,
      subscribe,
      stream,
      info,
      loadingInitial: loading.pairsInitial || loading.groupsInitial,
      loading: loading.pairs || loading.groups || loading.info,
    }),
    [derivatives, favorites, groups, info, loading, stream, subscribe],
  );

  return result;
};
